import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/** sessionStorage flag so a hard reload only happens once per tab session. */
export const CHUNK_RELOAD_STORAGE_KEY = "steddy:chunk-reload";

/**
 * True when a dynamic import failed because the hashed chunk is gone
 * (typical after a deploy while the SPA is still running an older main bundle).
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg)
  );
}

function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Reload the page once so the browser picks up a fresh index.html (and chunk
 * hashes). Returns true when a reload was triggered. Returns false when the
 * error is unrelated, or when we already reloaded once this session (avoid
 * infinite loops on a truly broken deploy). The flag is cleared only after a
 * successful chunk load via {@link lazyWithChunkReload}.
 */
export function reloadOnceForChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, "1");
  } catch {
    // sessionStorage unavailable — do not reload (could loop forever).
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Like React.lazy, but recovers from missing chunks after a deploy by doing a
 * single hard reload instead of leaving the user on the error page.
 */
// Match React.lazy's ComponentType<any> — props vary per imported module.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithChunkReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      clearChunkReloadFlag();
      return mod;
    } catch (error) {
      if (reloadOnceForChunkError(error)) {
        // Suspend forever while the reload runs.
        return new Promise(() => {});
      }
      throw error;
    }
  });
}
